import { css } from '@emotion/react'
import { Button, DataTable, FormControl, HFlow, Icon, Text, Tooltip, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { Box } from 'components/Box'
import { useErrorHandler } from 'components/error'
import { Form, FormRenderProps, PecFormApi, resetAllFieldStates, SubmitButton, TextField } from 'components/form'
import {
  IsPainelChamadasHabilitadoDocument,
  useCriarSalaUnidadeSaudeMutation,
  useRemoverSalaUnidadeSaudeMutation,
} from 'graphql/hooks.generated'
import { metaPath } from 'util/metaPath'
import {
  CadastrarSalaPainelChamadasModel,
  PermissaoPainelChamadas,
  SalaPainelChamadaRow,
} from 'view/chamada-painel/model-chamadaPainel'

import { cadastrarSalaPainelValidator } from '../validator-cadastrarSalaPainel'

const meta = metaPath<CadastrarSalaPainelChamadasModel>()

interface CadastrarSalaPainelChamadasBoxProps {
  salas: ReadonlyArray<SalaPainelChamadaRow>
  loadingSalas: boolean
  unidadeSaudeId: ID
  permissao: PermissaoPainelChamadas
  onCreate(): void
  onDelete(): void
}

export const CadastrarSalaPainelChamadasBox = (props: Readonly<CadastrarSalaPainelChamadasBoxProps>) => {
  const { salas, loadingSalas, unidadeSaudeId, onCreate, onDelete, permissao } = props

  const [criarSalaUnidadeSaude] = useCriarSalaUnidadeSaudeMutation()
  const [removerSalaUnidadeSaude, { loading: isLoadingDelete }] = useRemoverSalaUnidadeSaudeMutation()
  const handleRejection = useErrorHandler()
  const alert = useAlert()

  const clearForm = (form: PecFormApi<CadastrarSalaPainelChamadasModel>) => {
    form.reset()
    resetAllFieldStates(form)
  }

  const handleSubmit = (
    values: CadastrarSalaPainelChamadasModel,
    form: PecFormApi<CadastrarSalaPainelChamadasModel>
  ) => {
    return criarSalaUnidadeSaude({
      variables: {
        input: {
          nome: values.nome,
          unidadeSaudeId: unidadeSaudeId,
        },
      },
    })
      .then(() => {
        alert('success', 'Sala cadastrada com sucesso.')
        setTimeout(() => {
          clearForm(form)
        })
        onCreate()
      })
      .catch(handleRejection)
  }

  const handleDelete = (id: ID) => {
    removerSalaUnidadeSaude({
      variables: { id },
      refetchQueries: [{ query: IsPainelChamadasHabilitadoDocument, variables: { unidadeSaudeId } }],
    })
      .then(() => {
        alert('success', 'Sala excluída com sucesso.')
        onDelete()
      })
      .catch(handleRejection)
  }

  const renderForm = (formProps: FormRenderProps<CadastrarSalaPainelChamadasModel>) => {
    return (
      <HFlow>
        <TextField
          name={meta.nome}
          label='Nome da Sala'
          placeholder='Informe o nome da sala/consultório'
          required
          maxLength={50}
          style={styles.inputSala}
        />
        <FormControl label={<span>&nbsp;</span>}>
          <Tooltip text={permissao.hint ?? undefined}>
            <SubmitButton size='small' handleSubmit={formProps.handleSubmit} disabled={!permissao.enabled}>
              Adicionar
            </SubmitButton>
          </Tooltip>
        </FormControl>
      </HFlow>
    )
  }

  return (
    <Box>
      <VFlow>
        <Text>
          As salas ou locais cadastrados aqui serão selecionadas pelos profissionais no momento de chamar o cidadão para
          atendimento. A sala selecionada será apresentada no painel para direcionar o cidadão chamado ao local correto.
          Utilize nomes claros e coerentes com a sua unidade de saúde.
        </Text>
        <Form<CadastrarSalaPainelChamadasModel>
          onSubmit={handleSubmit}
          render={renderForm}
          validate={cadastrarSalaPainelValidator}
        />
        <DataTable<SalaPainelChamadaRow>
          rows={salas}
          loading={loadingSalas}
          columns={[
            { name: 'nome', header: 'Nome da sala', render: (row) => row.nome },
            {
              name: 'actions',
              header: '',
              align: 'right',
              render: (row) => (
                <Tooltip text={permissao.enabled ? 'Excluir' : permissao.hint ?? undefined}>
                  <Button
                    skin='ghost'
                    size='small'
                    onClick={() => handleDelete(row.id)}
                    loading={isLoadingDelete}
                    disabled={isLoadingDelete || !permissao.enabled}
                  >
                    <Icon icon='trashOutline' />
                  </Button>
                </Tooltip>
              ),
            },
          ]}
        />
      </VFlow>
    </Box>
  )
}

const styles = {
  inputSala: css`
    width: 23rem;
  `,
}
